/**
 * Wizard step 2 — the built prompt: meta chips, one Copy button (system +
 * user in one clipboard write), a readonly preview, and the handoff hint.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { Check, Copy } from 'lucide-react';

import { Button } from '@/components/ui';
import { useToast } from '@/providers/ToastProvider';

import type { AiWizardState } from '../use-ai-wizard';

const PromptStep: FC<{ wizard: AiWizardState }> = ({ wizard }) => {
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const preview = wizard.preview.data;
  if (!preview) return null;
  const meta = preview.meta;
  const statRows = Object.values(meta.itemCounts).reduce((a, b) => a + (b ?? 0), 0);
  const fullPrompt = `${preview.systemPrompt}\n\n${preview.userPrompt}`;

  const copy = async () => {
    await navigator.clipboard.writeText(fullPrompt);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
    toast.success('Copied', 'Prompt with live stats copied — paste it into your AI assistant.');
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <Chip>{meta.flowName}</Chip>
        <Chip>{statRows} stat rows</Chip>
        <Chip>{meta.metricCount} metrics</Chip>
        <Chip>{meta.tagCount} tags</Chip>
        <Chip>{(meta.dateRanges ?? [meta.dateRange]).join(' + ')}</Chip>
        {meta.truncated ? (
          <span
            className="rounded-full bg-warning/15 px-2 py-0.5 font-medium text-warning"
            title="Large flow — each stats table embeds only its top 40 rows by visits so the prompt stays compact. Nothing else is affected."
          >
            big flow — top 40 rows/layer embedded
          </span>
        ) : null}
      </div>
      <textarea
        readOnly
        value={fullPrompt}
        className="h-48 w-full resize-none rounded-md border border-border bg-muted/30 p-2 font-mono text-[10px] leading-snug"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={copy}>
          {copied ? (
            <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
          ) : (
            <Copy className="mr-1 h-3.5 w-3.5" aria-hidden />
          )}
          Copy prompt
        </Button>
        <span className="text-[11px] text-muted-foreground">
          Paste into Claude, Gemini or ChatGPT, then bring the JSON back here.
        </span>
        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={() => wizard.setStep('paste')}
        >
          I have the JSON →
        </Button>
      </div>
    </div>
  );
};

const Chip: FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full bg-muted px-2 py-0.5 font-medium tabular-nums text-muted-foreground">
    {children}
  </span>
);

export default PromptStep;
